import React, { useState, useEffect } from 'react';
import { InteractionMode } from '../types';

interface MotionGuideProps {
  interactionMode: InteractionMode;
  isCameraActive: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSelectMode?: (mode: InteractionMode) => void;
}

type GuideTab = 'camera' | 'cursor' | 'gyro' | 'touch';

export const MotionGuide: React.FC<MotionGuideProps> = ({
  interactionMode,
  isCameraActive,
  isOpen,
  onClose,
  onSelectMode
}) => {
  // Determine initial active tab based on active controller
  const getInitialTab = (): GuideTab => {
    if (isCameraActive || interactionMode === InteractionMode.CAMERA) return 'camera';
    if (interactionMode === InteractionMode.TOUCH) return 'touch';
    return 'cursor';
  };

  const [activeTab, setActiveTab] = useState<GuideTab>(getInitialTab);

  // Sync tab when opening modal
  useEffect(() => {
    if (isOpen) {
      setActiveTab(getInitialTab());
    }
  }, [isOpen, isCameraActive, interactionMode]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      id="motion-guide-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="motion-guide-title"
      className="fixed inset-0 z-[200] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-slate-900/95 border border-white/15 rounded-3xl max-w-xl w-full p-5 sm:p-6 text-white shadow-2xl shadow-black/80 space-y-5 relative overflow-hidden my-auto pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Decorative background ambient glows */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-sky-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-indigo-500/15 rounded-full blur-3xl pointer-events-none" />

        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 pb-4 relative z-10">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-sky-500/20 text-sky-300 border border-sky-500/30 shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
              </svg>
            </div>
            <div>
              <h2 id="motion-guide-title" className="text-base sm:text-lg font-bold text-white tracking-wide">
                How Movement Conducts Music
              </h2>
              <p className="text-xs text-slate-400">Interactive Physical Gesture & Control Mapping</p>
            </div>
          </div>
          <button 
            id="close-motion-guide-btn"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 active:scale-95 text-slate-300 hover:text-white flex items-center justify-center text-sm font-bold transition border border-white/10 shrink-0 cursor-pointer"
            aria-label="Close motion guide"
          >
            ✕
          </button>
        </div>

        {/* Interactive Mode Tabs */}
        <div className="flex items-center gap-1.5 p-1 bg-slate-950/80 rounded-2xl border border-white/10 relative z-10 overflow-x-auto">
          <button
            onClick={() => setActiveTab('camera')}
            className={`flex-1 min-w-[100px] py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
              activeTab === 'camera'
                ? 'bg-sky-500 text-white shadow-md shadow-sky-500/20'
                : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
            }`}
          >
            <span>🖐️</span>
            <span>Webcam</span>
            {isCameraActive && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
          </button>

          <button
            onClick={() => setActiveTab('cursor')}
            className={`flex-1 min-w-[100px] py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
              activeTab === 'cursor'
                ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
            }`}
          >
            <span>🖱️</span>
            <span>Cursor</span>
            {!isCameraActive && interactionMode === InteractionMode.MOTION && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
          </button>

          <button
            onClick={() => setActiveTab('gyro')}
            className={`flex-1 min-w-[100px] py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
              activeTab === 'gyro'
                ? 'bg-teal-600 text-white shadow-md shadow-teal-600/20'
                : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
            }`}
          >
            <span>📱</span>
            <span>Phone Tilt</span>
          </button>

          <button
            onClick={() => setActiveTab('touch')}
            className={`flex-1 min-w-[100px] py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
              activeTab === 'touch'
                ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
            }`}
          >
            <span>👆</span>
            <span>Touch</span>
            {interactionMode === InteractionMode.TOUCH && !isCameraActive && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
          </button>
        </div>

        {/* Tab Content Cards */}
        <div className="space-y-4 relative z-10 text-sm">
          {activeTab === 'camera' && (
            <div className="space-y-3 animate-fade-in">
              <div className="flex items-center justify-between text-xs font-semibold text-sky-300 bg-sky-950/60 px-3 py-1.5 rounded-xl border border-sky-500/30">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
                  <span>WEBCAM BODY CONDUCTING</span>
                </div>
                <span className="text-[11px] text-sky-400/80">Hands-Free Vision Tracker</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="p-3 rounded-2xl bg-slate-950/70 border border-white/10 space-y-1">
                  <div className="flex items-center gap-2 text-indigo-300 font-bold text-xs">
                    <span className="text-sm">🖐️</span>
                    <span>Right Hand (Melody Pitch)</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Raise your right hand higher into the air to play higher pitched notes along the sacred scale.
                  </p>
                </div>

                <div className="p-3 rounded-2xl bg-slate-950/70 border border-white/10 space-y-1">
                  <div className="flex items-center gap-2 text-teal-300 font-bold text-xs">
                    <span className="text-sm">🖐️</span>
                    <span>Left Hand (Resonance & Bass)</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Move your left hand vertically to adjust lowpass filter brightness, warmth, and sub-bass depth.
                  </p>
                </div>

                <div className="p-3 rounded-2xl bg-slate-950/70 border border-white/10 space-y-1">
                  <div className="flex items-center gap-2 text-cyan-300 font-bold text-xs">
                    <span className="text-sm">↔️</span>
                    <span>Arm Span (Spatial Reverb)</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Spread both arms wide to expand stereo diffusion, delay reflections, and lush ambient reverb.
                  </p>
                </div>

                <div className="p-3 rounded-2xl bg-slate-950/70 border border-white/10 space-y-1">
                  <div className="flex items-center gap-2 text-amber-300 font-bold text-xs">
                    <span className="text-sm">⚡</span>
                    <span>Conducting Speed</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Gentle, slow arm motions create peaceful blooms; rapid gestures trigger dynamic melodic ripples.
                  </p>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-slate-950/60 border border-white/10 flex items-start gap-2.5">
                <span className="text-base shrink-0">💡</span>
                <p className="text-xs text-slate-400 leading-relaxed">
                  <strong className="text-slate-200">Optimal Placement: </strong>
                  Step back 5–7 feet (1.5–2m) with the camera at chest level so both your hands and torso are visible.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'cursor' && (
            <div className="space-y-3 animate-fade-in">
              <div className="flex items-center justify-between text-xs font-semibold text-indigo-300 bg-indigo-950/60 px-3 py-1.5 rounded-xl border border-indigo-500/30">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" />
                  <span>DESKTOP MOUSE CONDUCTING</span>
                </div>
                <span className="text-[11px] text-indigo-400/80">Fluid 2D Cursor Mapping</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="p-3 rounded-2xl bg-slate-950/70 border border-white/10 space-y-1">
                  <div className="flex items-center gap-2 text-indigo-300 font-bold text-xs">
                    <span className="text-sm">↕️</span>
                    <span>Vertical Position (Y)</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Move your mouse towards the top of your screen to ascend to higher octave harmonic notes.
                  </p>
                </div>

                <div className="p-3 rounded-2xl bg-slate-950/70 border border-white/10 space-y-1">
                  <div className="flex items-center gap-2 text-cyan-300 font-bold text-xs">
                    <span className="text-sm">↔️</span>
                    <span>Horizontal Position (X)</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Move left and right across the canvas to pan audio and open filter frequency cutoff.
                  </p>
                </div>

                <div className="p-3 rounded-2xl bg-slate-950/70 border border-white/10 space-y-1">
                  <div className="flex items-center gap-2 text-amber-300 font-bold text-xs">
                    <span className="text-sm">⚡</span>
                    <span>Movement Velocity</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Faster mouse movement increases note velocity and excites the visual somatic ripple particles.
                  </p>
                </div>

                <div className="p-3 rounded-2xl bg-slate-950/70 border border-white/10 space-y-1">
                  <div className="flex items-center gap-2 text-emerald-300 font-bold text-xs">
                    <span className="text-sm">🖱️</span>
                    <span>Stage Click to Pause</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Click anywhere on the visualizer canvas to toggle active performance conducting on or off.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'gyro' && (
            <div className="space-y-3 animate-fade-in">
              <div className="flex items-center justify-between text-xs font-semibold text-teal-300 bg-teal-950/60 px-3 py-1.5 rounded-xl border border-teal-500/30">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
                  <span>SMARTPHONE GYROSCOPE TILT</span>
                </div>
                <span className="text-[11px] text-teal-400/80">Physical Device Sensors</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="p-3 rounded-2xl bg-slate-950/70 border border-white/10 space-y-1">
                  <div className="flex items-center gap-2 text-teal-300 font-bold text-xs">
                    <span className="text-sm">📱</span>
                    <span>Forward / Backward Pitch (Beta)</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Tilt your phone screen forward or backward in your hand to traverse through musical scale degrees.
                  </p>
                </div>

                <div className="p-3 rounded-2xl bg-slate-950/70 border border-white/10 space-y-1">
                  <div className="flex items-center gap-2 text-cyan-300 font-bold text-xs">
                    <span className="text-sm">🔄</span>
                    <span>Left / Right Roll (Gamma)</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Roll your phone side to side to modulate analog low-pass filters and stereo balance.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'touch' && (
            <div className="space-y-3 animate-fade-in">
              <div className="flex items-center justify-between text-xs font-semibold text-purple-300 bg-purple-950/60 px-3 py-1.5 rounded-xl border border-purple-500/30">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
                  <span>MULTI-TOUCH CONDUCTING</span>
                </div>
                <span className="text-[11px] text-purple-400/80">Direct Screen Interaction</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="p-3 rounded-2xl bg-slate-950/70 border border-white/10 space-y-1">
                  <div className="flex items-center gap-2 text-purple-300 font-bold text-xs">
                    <span className="text-sm">👆</span>
                    <span>Tap & Glide</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Touch anywhere on the visualizer and drag your fingers to sculpt sound waves in real time.
                  </p>
                </div>

                <div className="p-3 rounded-2xl bg-slate-950/70 border border-white/10 space-y-1">
                  <div className="flex items-center gap-2 text-pink-300 font-bold text-xs">
                    <span className="text-sm">✨</span>
                    <span>Somatic Release</span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Releasing your finger lets the current harmony ring out with natural resonant acoustic decay.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Action */}
        <div className="pt-3 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
            <span className="text-amber-400 font-bold">⚡ Tip:</span>
            <span>Use the <strong className="text-slate-200 font-semibold">Motion Sensitivity</strong> control in the bottom dock to tailor response reach to your posture or seated setup.</span>
          </div>
          <button
            id="got-it-motion-guide-btn"
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2.5 bg-sky-600 hover:bg-sky-500 active:scale-95 text-white font-bold text-xs rounded-xl shadow-lg shadow-sky-950/40 transition cursor-pointer shrink-0"
          >
            Got It • Back to Music
          </button>
        </div>
      </div>
    </div>
  );
};
